import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { InputNumber } from "@/components/InputNumber";
import { formatLocaleCurrency } from "@/utils/formatUtils";
import { isDefined } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { App as AntApp, Checkbox, Divider, Flex, Form, Input, Radio } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { ClientType } from "../../../../../client/enums";
import { CalcAmountTag } from "../../../components/recalculation/CalcAmountTag";
import { CalcCheckableTag } from "../../../components/recalculation/CalcCheckableTag";
import { CalcDiscountsButton } from "../../../components/recalculation/CalcDiscountsButton";
import { CalcOptionTag } from "../../../components/recalculation/CalcOptionTag";
import { CalcRecalculationCard } from "../../../components/recalculation/CalcRecalculationCard";
import { CalcValueTag } from "../../../components/recalculation/CalcValueTag";
import { hasFieldChanges } from "../../../utils";
import { BuildingType, Liability, LoanReinsuranceAmount, RealtyComplicityAmount } from "../../enums";
import type {
  RealtyCalc,
  RealtyCalcCrossSelling,
  RealtyCalcGeneralInsuranceData,
  RealtyCalcHouseholdInsuranceData,
  RealtyCalcLiabilityInsuranceData,
  RealtyCalcRealtyInsuranceData,
} from "../../types";
import { isRealtyCrossSellingDiscountAvailable, isRealtyReinsuranceAvailable } from "../../utils";
import { RealtyAllRiskReinsuranceDescription } from "../RealtyAllRiskReinsuranceDescription";
import { RealtyCalcOtherInsuranceAmounts } from "./RealtyCalcOtherInsuranceAmounts";

const NO_COMPLICITY = "NO_COMPLICITY";

const GENERAL_INSURANCE_FIELD_NAMES = [
  "cyberneticReinsurance",
  "warrantyReinsurance",
  "cyclingReinsurance",
  "loanReinsurance",
  "loanReinsuranceAmount",
  "rentReinsurance",
  "rentReinsuranceAmount",
  "allRiskReinsurance",
  "complicity",
  "complicityAmount",
] satisfies (keyof RealtyCalcGeneralInsuranceData)[];

type GeneralInsuranceFields = Pick<RealtyCalcGeneralInsuranceData, (typeof GENERAL_INSURANCE_FIELD_NAMES)[number]>;

const TAG_FIELD_NAMES: (keyof GeneralInsuranceFields)[] = [
  "cyberneticReinsurance",
  "warrantyReinsurance",
  "cyclingReinsurance",
  "allRiskReinsurance",
];

const HIDDEN_GENERAL_INSURANCE_FIELD_NAMES = GENERAL_INSURANCE_FIELD_NAMES.filter(
  (name) => !TAG_FIELD_NAMES.includes(name),
);

const CROSS_SELLING_DISCOUNTS = [
  "csobContract",
  "generaliContract",
  "unionHealthContract",
  "uniqaContractNumber",
  "premiumContractNumber",
  "colonnadeDiscountCode",
] satisfies (keyof RealtyCalcCrossSelling)[];

const CROSS_SELLING_FIELD_NAMES: (keyof RealtyCalcCrossSelling)[] = [
  ...CROSS_SELLING_DISCOUNTS,
  "unionHealthContractNumber",
];

const INSURANCE_AMOUNT_RULES = [validations.notNull, validations.minNumber(1), validations.multipleOf100];

const PREMIUM_FIELD_NAMES = [
  "electronicsInsuranceAmount",
  "artworksInsuranceAmount",
  "valuablesInsuranceAmount",
] satisfies (keyof RealtyCalcHouseholdInsuranceData)[];

type InsuranceAmount = { insuranceAmount?: number };

export type RecalculationForm = {
  realtyInsuranceData?: Pick<RealtyCalcRealtyInsuranceData, "insuranceAmount"> & {
    nearbyBuildingReinsurances?: InsuranceAmount[];
  };
  householdInsuranceData?: Pick<
    RealtyCalcHouseholdInsuranceData,
    "insuranceAmount" | (typeof PREMIUM_FIELD_NAMES)[number]
  > & {
    equipmentReinsurances?: InsuranceAmount[];
  };
  liabilityInsuranceData: RealtyCalcLiabilityInsuranceData;
  generalInsuranceData: GeneralInsuranceFields & {
    crossSelling: RealtyCalcCrossSelling;
  };
};

const isListAmountChanged = (calculated: InsuranceAmount[] = [], current: InsuranceAmount[] = []) =>
  calculated.some((item, index) => item.insuranceAmount !== current[index]?.insuranceAmount);

const clearInactiveAmounts = <T extends GeneralInsuranceFields>(data: T): T => ({
  ...data,
  loanReinsuranceAmount: data.loanReinsurance ? data.loanReinsuranceAmount : undefined,
  rentReinsuranceAmount: data.rentReinsurance ? data.rentReinsuranceAmount : undefined,
  complicityAmount: data.complicity ? data.complicityAmount : undefined,
});

const normalizeLiability = ({
  liability,
  extendedLiability,
}: RealtyCalcLiabilityInsuranceData): RealtyCalcLiabilityInsuranceData => ({
  liability,
  extendedLiability: liability !== Liability.NONE ? extendedLiability : undefined,
});

const normalizeCrossSelling = ({
  uniqaContractNumber,
  premiumContractNumber,
  colonnadeDiscountCode,
  ...crossSelling
}: RealtyCalcCrossSelling): RealtyCalcCrossSelling => ({
  ...crossSelling,
  uniqaContractNumber: uniqaContractNumber?.trim() || undefined,
  premiumContractNumber: premiumContractNumber?.trim() || undefined,
  colonnadeDiscountCode: colonnadeDiscountCode?.trim() || undefined,
});

type CrossSellingDiscountsProps = {
  policyHolderType?: ClientType;
};

const CrossSellingDiscounts = ({ policyHolderType }: CrossSellingDiscountsProps) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance<RecalculationForm>();
  const crossSelling = Form.useWatch(["generalInsuranceData", "crossSelling"], form);

  const normalizedCrossSelling = normalizeCrossSelling(crossSelling ?? {});
  const activeCount = CROSS_SELLING_DISCOUNTS.filter(
    (discount) =>
      isRealtyCrossSellingDiscountAvailable(discount, policyHolderType) && !!normalizedCrossSelling[discount],
  ).length;

  return (
    <CalcDiscountsButton count={activeCount} title={t("calc.realty.sections.crossSelling")}>
      <Form.Item
        name={["generalInsuranceData", "crossSelling", "csobContract"]}
        valuePropName="checked"
        style={{ marginBottom: 0 }}
      >
        <Checkbox>{t("calc.realty.attrs.generalInsuranceData.crossSelling.csobContract")}</Checkbox>
      </Form.Item>

      <Form.Item
        name={["generalInsuranceData", "crossSelling", "generaliContract"]}
        valuePropName="checked"
        style={{ marginBottom: 0 }}
      >
        <Checkbox>{t("calc.realty.attrs.generalInsuranceData.crossSelling.generaliContract")}</Checkbox>
      </Form.Item>

      {isRealtyCrossSellingDiscountAvailable("unionHealthContract", policyHolderType) && (
        <Form.Item
          name={["generalInsuranceData", "crossSelling", "unionHealthContract"]}
          valuePropName="checked"
          style={{ marginBottom: 0 }}
        >
          <Checkbox>{t("calc.realty.attrs.generalInsuranceData.crossSelling.unionHealthContract")}</Checkbox>
        </Form.Item>
      )}

      {["uniqaContractNumber", "premiumContractNumber", "colonnadeDiscountCode"].map((discount) => (
        <Form.Item
          key={discount}
          name={["generalInsuranceData", "crossSelling", discount]}
          label={t(`calc.realty.attrs.generalInsuranceData.crossSelling.${discount}`)}
          rules={[validations.size(1, 64)]}
          style={{ marginBottom: 0 }}
        >
          <Input allowClear />
        </Form.Item>
      ))}
    </CalcDiscountsButton>
  );
};

type Props = {
  calcData: RealtyCalc;
  onRecalculate: (calcData: RealtyCalc) => void;
};

export const RealtyCalcRecalculationForm = ({ calcData, onRecalculate }: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const [form] = Form.useForm<RecalculationForm>();
  const values = Form.useWatch([], form);

  useEffect(() => {
    form.resetFields();
  }, [calcData]);

  const { realtyInsuranceData, householdInsuranceData, liabilityInsuranceData, generalInsuranceData, clientsData } =
    calcData;

  const policyHolderType = isDefined(clientsData.policyHolderIndex)
    ? clientsData.clients?.[clientsData.policyHolderIndex]?.client.type
    : undefined;

  const isHouse = [BuildingType.HOUSE, BuildingType.RECREATIONAL_BUILDING].includes(calcData.generalBuildingData.type);

  const realtyAmount = values?.realtyInsuranceData?.insuranceAmount;
  const householdAmount = values?.householdInsuranceData?.insuranceAmount;
  const liability = values?.liabilityInsuranceData?.liability;
  const complicity = values?.generalInsuranceData?.complicity;
  const complicityAmount = values?.generalInsuranceData?.complicityAmount;

  const isChanged =
    !!values?.generalInsuranceData &&
    (realtyAmount !== realtyInsuranceData?.insuranceAmount ||
      householdAmount !== householdInsuranceData?.insuranceAmount ||
      isListAmountChanged(
        realtyInsuranceData?.nearbyBuildingReinsurances,
        values.realtyInsuranceData?.nearbyBuildingReinsurances,
      ) ||
      isListAmountChanged(
        householdInsuranceData?.equipmentReinsurances,
        values.householdInsuranceData?.equipmentReinsurances,
      ) ||
      hasFieldChanges<Partial<Pick<RealtyCalcHouseholdInsuranceData, (typeof PREMIUM_FIELD_NAMES)[number]>>>(
        PREMIUM_FIELD_NAMES,
        values.householdInsuranceData ?? {},
        householdInsuranceData ?? {},
      ) ||
      hasFieldChanges(
        ["liability", "extendedLiability"],
        normalizeLiability(values.liabilityInsuranceData),
        normalizeLiability(liabilityInsuranceData),
      ) ||
      hasFieldChanges<GeneralInsuranceFields>(
        GENERAL_INSURANCE_FIELD_NAMES,
        clearInactiveAmounts(values.generalInsuranceData),
        clearInactiveAmounts(generalInsuranceData),
      ) ||
      hasFieldChanges(
        CROSS_SELLING_FIELD_NAMES,
        normalizeCrossSelling(values.generalInsuranceData.crossSelling ?? {}),
        normalizeCrossSelling(generalInsuranceData.crossSelling),
      ));

  const handleRecalculate = () => {
    form.validateFields().then(
      (formValues) => {
        const { crossSelling, ...generalInsuranceFields } = formValues.generalInsuranceData;

        onRecalculate({
          ...calcData,
          realtyInsuranceData: realtyInsuranceData && {
            ...realtyInsuranceData,
            ...formValues.realtyInsuranceData,
            nearbyBuildingReinsurances: realtyInsuranceData.nearbyBuildingReinsurances.map((building, index) => ({
              ...building,
              ...formValues.realtyInsuranceData?.nearbyBuildingReinsurances?.[index],
            })),
          },
          householdInsuranceData: householdInsuranceData && {
            ...householdInsuranceData,
            ...formValues.householdInsuranceData,
            equipmentReinsurances: householdInsuranceData.equipmentReinsurances.map((equipment, index) => ({
              ...equipment,
              ...formValues.householdInsuranceData?.equipmentReinsurances?.[index],
            })),
          },
          liabilityInsuranceData: normalizeLiability(formValues.liabilityInsuranceData),
          generalInsuranceData: {
            ...generalInsuranceData,
            ...clearInactiveAmounts(generalInsuranceFields),
            crossSelling: normalizeCrossSelling(crossSelling),
          },
        });
      },
      () => notification.error({ title: t("common.error"), description: t("common.validations.formError") }),
    );
  };

  const formatAmount = (amount?: number) => (isDefined(amount) ? formatLocaleCurrency(amount, 0) : "–");

  return (
    <Form
      form={form}
      name="realtyCalcRecalculationForm"
      layout="vertical"
      initialValues={{ realtyInsuranceData, householdInsuranceData, liabilityInsuranceData, generalInsuranceData }}
    >
      {realtyInsuranceData && <HiddenInput name={["realtyInsuranceData", "insuranceAmount"]} />}
      {householdInsuranceData && <HiddenInput name={["householdInsuranceData", "insuranceAmount"]} />}

      {realtyInsuranceData && (
        <Form.List name={["realtyInsuranceData", "nearbyBuildingReinsurances"]}>
          {(fields) => fields.map((field) => <HiddenInput key={field.key} name={[field.name, "insuranceAmount"]} />)}
        </Form.List>
      )}

      {householdInsuranceData && (
        <Form.List name={["householdInsuranceData", "equipmentReinsurances"]}>
          {(fields) => fields.map((field) => <HiddenInput key={field.key} name={[field.name, "insuranceAmount"]} />)}
        </Form.List>
      )}

      {householdInsuranceData &&
        PREMIUM_FIELD_NAMES.map((name) => <HiddenInput key={name} name={["householdInsuranceData", name]} />)}

      <HiddenInput name={["liabilityInsuranceData", "liability"]} />
      <HiddenInput name={["liabilityInsuranceData", "extendedLiability"]} />

      {HIDDEN_GENERAL_INSURANCE_FIELD_NAMES.map((name) => (
        <HiddenInput key={name} name={["generalInsuranceData", name]} />
      ))}

      {CROSS_SELLING_FIELD_NAMES.map((name) => (
        <HiddenInput key={name} name={["generalInsuranceData", "crossSelling", name]} />
      ))}

      <CalcRecalculationCard
        title={t("calc.realty.sections.insuranceAndReinsurances")}
        isChanged={isChanged}
        onRecalculate={handleRecalculate}
        discounts={<CrossSellingDiscounts policyHolderType={policyHolderType} />}
      >
        <Flex wrap gap="small" style={{ width: "100%" }}>
          {realtyInsuranceData && (
            <CalcValueTag
              label={t("calc.realty.sections.realtyInsurance")}
              value={formatAmount(realtyAmount)}
              error={!isDefined(realtyAmount)}
            >
              <Form.Item
                name={["realtyInsuranceData", "insuranceAmount"]}
                label={
                  isHouse ? (
                    <LabelWithTooltip
                      label={t("calc.realty.attrs.realtyInsuranceData.insuranceAmountWithNearbyBuildings")}
                      tooltip={t("calc.realty.helpers.realtyInsuranceAmountDesc")}
                    />
                  ) : (
                    t("calc.realty.attrs.realtyInsuranceData.insuranceAmount")
                  )
                }
                rules={INSURANCE_AMOUNT_RULES}
                style={{ marginBottom: 0 }}
              >
                <InputNumber addon="euro" step={100} />
              </Form.Item>
            </CalcValueTag>
          )}

          {householdInsuranceData && (
            <CalcValueTag
              label={t("calc.realty.sections.householdInsurance")}
              value={formatAmount(householdAmount)}
              error={!isDefined(householdAmount)}
            >
              <Form.Item
                name={["householdInsuranceData", "insuranceAmount"]}
                label={t("calc.realty.attrs.householdInsuranceData.insuranceAmount")}
                rules={INSURANCE_AMOUNT_RULES}
                style={{ marginBottom: 0 }}
              >
                <InputNumber addon="euro" step={100} />
              </Form.Item>
            </CalcValueTag>
          )}

          <CalcValueTag
            label={t("calc.realty.enums.liability._label")}
            value={
              liability &&
              t(`calc.realty.enums.liability.${liability}`) +
                (liability !== Liability.NONE && values?.liabilityInsuranceData?.extendedLiability
                  ? `, ${t("calc.realty.helpers.extendedLiabilityShort")}`
                  : "")
            }
          >
            <Form.Item
              name={["liabilityInsuranceData", "liability"]}
              label={t("calc.realty.enums.liability._label")}
              style={{ marginBottom: 0 }}
            >
              <Radio.Group
                orientation="vertical"
                options={Object.keys(Liability).map((option) => ({
                  value: option,
                  label: t(`calc.realty.enums.liability.${option}`),
                }))}
              />
            </Form.Item>

            {liability !== Liability.NONE && (
              <Form.Item
                name={["liabilityInsuranceData", "extendedLiability"]}
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox>{t("calc.realty.attrs.liabilityInsuranceData.extendedLiability")}</Checkbox>
              </Form.Item>
            )}
          </CalcValueTag>

          <CalcValueTag
            label={t("calc.realty.enums.complicityAmount._label")}
            value={
              complicity && complicityAmount
                ? t(`calc.realty.enums.complicityAmount.${complicityAmount}`)
                : t("calc.realty.helpers.noComplicity")
            }
          >
            <Form.Item label={t("calc.realty.enums.complicityAmount._label")} style={{ marginBottom: 0 }}>
              <Radio.Group
                orientation="vertical"
                value={complicity ? complicityAmount : NO_COMPLICITY}
                options={[
                  { value: NO_COMPLICITY, label: t("calc.realty.helpers.noComplicity") },
                  ...Object.keys(RealtyComplicityAmount).map((amount) => ({
                    value: amount,
                    label: t(`calc.realty.enums.complicityAmount.${amount}`),
                  })),
                ]}
                onChange={(e) =>
                  form.setFields([
                    { name: ["generalInsuranceData", "complicity"], value: e.target.value !== NO_COMPLICITY },
                    {
                      name: ["generalInsuranceData", "complicityAmount"],
                      value: e.target.value !== NO_COMPLICITY ? e.target.value : undefined,
                    },
                  ])
                }
              />
            </Form.Item>
          </CalcValueTag>

          <RealtyCalcOtherInsuranceAmounts calcData={calcData} />
        </Flex>

        <Divider className="divider-subheader" titlePlacement="start" style={{ margin: "4px 0" }}>
          {t("calc.realty.sections.reinsurances")}
        </Divider>

        <Flex wrap gap="small" style={{ width: "100%" }}>
          <Form.Item name={["generalInsuranceData", "cyberneticReinsurance"]} valuePropName="checked" noStyle>
            <CalcCheckableTag>
              <LabelWithTooltip
                label={t("calc.realty.attrs.generalInsuranceData.cyberneticReinsurance")}
                tooltip={t("calc.realty.helpers.cyberneticReinsuranceDesc")}
              />
            </CalcCheckableTag>
          </Form.Item>
          <Form.Item name={["generalInsuranceData", "warrantyReinsurance"]} valuePropName="checked" noStyle>
            <CalcCheckableTag>
              <LabelWithTooltip
                label={t("calc.realty.attrs.generalInsuranceData.warrantyReinsurance")}
                tooltip={t("calc.realty.helpers.warrantyReinsuranceDesc")}
              />
            </CalcCheckableTag>
          </Form.Item>
          <Form.Item name={["generalInsuranceData", "cyclingReinsurance"]} valuePropName="checked" noStyle>
            <CalcCheckableTag>
              <LabelWithTooltip
                label={t("calc.realty.attrs.generalInsuranceData.cyclingReinsurance")}
                tooltip={t("calc.realty.helpers.cyclingReinsuranceDesc")}
              />
            </CalcCheckableTag>
          </Form.Item>

          {isRealtyReinsuranceAvailable("loanReinsurance", !!realtyInsuranceData) && (
            <CalcOptionTag
              name={["generalInsuranceData", "loanReinsurance"]}
              optionName={["generalInsuranceData", "loanReinsuranceAmount"]}
              label={t("calc.realty.attrs.generalInsuranceData.loanReinsurance")}
              optionsLabel={t("calc.realty.enums.loanReinsuranceAmount._label")}
              options={Object.keys(LoanReinsuranceAmount).map((amount) => ({
                value: amount,
                label: t("calc.realty.enums.loanReinsuranceAmount." + amount),
              }))}
            />
          )}

          <CalcAmountTag
            name={["generalInsuranceData", "rentReinsurance"]}
            amountName={["generalInsuranceData", "rentReinsuranceAmount"]}
            label={t("calc.realty.attrs.generalInsuranceData.rentReinsurance")}
            amountLabel={
              <LabelWithTooltip
                label={t("calc.realty.attrs.generalInsuranceData.rentReinsuranceAmount")}
                tooltip={t("calc.realty.helpers.rentReinsuranceAmountDesc")}
              />
            }
            rules={[validations.minNumber(1), validations.maxNumber(24_000), validations.multipleOf100]}
            step={100}
          />

          <Form.Item name={["generalInsuranceData", "allRiskReinsurance"]} valuePropName="checked" noStyle>
            <CalcCheckableTag>
              <LabelWithPopover
                label={t("calc.realty.attrs.generalInsuranceData.allRiskReinsurance")}
                popoverContent={<RealtyAllRiskReinsuranceDescription />}
              />
            </CalcCheckableTag>
          </Form.Item>
        </Flex>
      </CalcRecalculationCard>
    </Form>
  );
};
